import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { TokenContext } from "./TokenContext";
import { CreatorContext } from "./CreatorContext";
import { requestsClient } from "../utils/RequestsClients";
import { loadFont, fontFamilyToCssStack } from "../utils/loadFont";
import { withWebTemplateDefaults } from "../utils/getDefaultWebTemplate";
import { resolvePageBackground, getMoodOverride } from "../utils/pageBackground";

export const WebTemplateContext = createContext({
	webTemplate: null,
	loading: true,
	isDemoContent: null,
});

export const WebTemplateProvider = ({ children }) => {
	const { pathId } = useContext(TokenContext);
	const { creator } = useContext(CreatorContext);
	const [rawTemplate, setRawTemplate] = useState(null);
	const [loading, setLoading] = useState(true);

	const { template: mergedTemplate, isDemoContent } = useMemo(
		() =>
			withWebTemplateDefaults(rawTemplate, creator, {
				webShowDefaults: rawTemplate?.webShowDefaults ?? true,
			}),
		[rawTemplate, creator]
	);
	const webTemplate = useMemo(() => {
		if (loading || !mergedTemplate) return null;
		const backgroundColor = resolvePageBackground(
			mergedTemplate.backgroundColor,
			getMoodOverride() || mergedTemplate.designTokens?.mood
		);
		const withBg =
			backgroundColor && backgroundColor !== mergedTemplate.backgroundColor
				? { ...mergedTemplate, backgroundColor }
				: mergedTemplate;
		const ff = withBg.fontFamily;
		if (!ff) return withBg;
		return { ...withBg, fontFamily: fontFamilyToCssStack(ff) };
	}, [loading, mergedTemplate]);
	const templateDemoFlag = useMemo(
		() => (loading ? null : isDemoContent),
		[loading, isDemoContent]
	);
	const fontFamily = webTemplate?.fontFamily;

	useEffect(() => {
		if (!fontFamily) return;
		loadFont(fontFamily);
		document.documentElement.style.setProperty("--main-font", fontFamily);
	}, [fontFamily]);

	useEffect(() => {
		const tokens = webTemplate?.designTokens;
		const root = document.documentElement;
		if (!tokens || typeof tokens !== "object") return;

		const SPACING_MAP = { compact: "48px", normal: "80px", spacious: "120px" };
		const SHADOW_MAP = {
			none: "none",
			sm: "0 1px 3px rgba(0,0,0,0.08)",
			md: "0 4px 12px rgba(0,0,0,0.1)",
			lg: "0 10px 30px rgba(0,0,0,0.15)",
		};

		if (tokens.borderRadius) root.style.setProperty("--dt-border-radius", tokens.borderRadius);
		else root.style.removeProperty("--dt-border-radius");
		if (tokens.buttonRadius) root.style.setProperty("--dt-button-radius", tokens.buttonRadius);
		else root.style.removeProperty("--dt-button-radius");
		if (tokens.buttonStyle) root.style.setProperty("--dt-button-style", tokens.buttonStyle);
		if (tokens.cardShadow) root.style.setProperty("--dt-card-shadow", SHADOW_MAP[tokens.cardShadow] || SHADOW_MAP.md);
		else root.style.removeProperty("--dt-card-shadow");
		if (tokens.heroOverlay) root.style.setProperty("--dt-hero-overlay", tokens.heroOverlay);
		// Solo aplicar altura/spacing de hero cuando el token lo pide explícitamente.
		// Si no, dejar auto/0 para no romper banners compactos (shop5/shop6).
		if (tokens.heroHeight) root.style.setProperty("--dt-hero-height", tokens.heroHeight);
		else root.style.removeProperty("--dt-hero-height");
		if (tokens.sectionSpacing) root.style.setProperty("--dt-section-spacing", SPACING_MAP[tokens.sectionSpacing] || SPACING_MAP.normal);
		else root.style.setProperty("--dt-section-spacing", "0px");
		if (tokens.navbarStyle) root.style.setProperty("--dt-navbar-style", tokens.navbarStyle);
		if (webTemplate?.mainColor) {
			root.style.setProperty("--dt-accent-color", webTemplate.mainColor);
		} else if (tokens.accentColor) {
			root.style.setProperty("--dt-accent-color", tokens.accentColor);
		}
		if (tokens.imageStyle) root.style.setProperty("--dt-image-style", tokens.imageStyle);
		if (tokens.animationLevel) root.style.setProperty("--dt-animation-level", tokens.animationLevel);

		const body = document.body;
		body.classList.remove("dt-heading-uppercase", "dt-heading-italic");
		if (tokens.headingStyle === "uppercase") body.classList.add("dt-heading-uppercase");
		if (tokens.headingStyle === "italic") body.classList.add("dt-heading-italic");

		if (webTemplate?.mainColor) root.style.setProperty("--dt-main-color", webTemplate.mainColor);
		if (webTemplate?.backgroundColor) {
			root.style.setProperty("--dt-bg-color", webTemplate.backgroundColor);
			root.style.backgroundColor = webTemplate.backgroundColor;
			document.body.style.backgroundColor = webTemplate.backgroundColor;
		}
	}, [webTemplate]);

	useEffect(() => {
		if (!pathId) return;
		const fetchTemplate = async () => {
			try {
				setLoading(true);
				console.log("📡 Cargando web template para path:", pathId);
				const res = await requestsClient.get(
					"web-template",
					undefined,
					undefined,
					pathId // ✅ este es el valor correcto
				);
				setRawTemplate(res.data);
			} catch (err) {
				console.error("Error cargando web template", err);
				setRawTemplate(null);
			} finally {
				setLoading(false);
			}
		};
		fetchTemplate();
	}, [pathId]);

	return (
		<WebTemplateContext.Provider
			value={{
				webTemplate,
				loading,
				isDemoContent: templateDemoFlag,
			}}
		>
			{children}
		</WebTemplateContext.Provider>
	);
};
